import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSettings } from '../context/SettingsContext';
import { getCategories, Category } from '../api/products';

const Footer: React.FC = () => {
    const { t, i18n } = useTranslation();
    const { getSetting } = useSettings();
    const [categories, setCategories] = useState<Category[]>([]);

    useEffect(() => {
        const fetchCategories = async () => {
            try {
                const data = await getCategories();
                setCategories(data);
            } catch (error) {
                console.error('Failed to fetch categories', error);
            }
        };
        fetchCategories();
    }, []);

    const isRTL = i18n.dir() === 'rtl';

    return (
        <footer className="bg-neutral-900 text-neutral-300 py-12">
            <div className="container mx-auto px-4">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
                    {/* Brand */}
                    <div>
                        <img src="/logo.png" alt={getSetting('site_name', 'Farhan')} className="h-12 w-auto mb-4 bg-white rounded-lg p-1" />
                        <p className="text-sm leading-relaxed">
                            {getSetting('footer_description', t('home.heroSubtitle'))}
                        </p>
                    </div>

                    {/* Links */}
                    <div>
                        <h4 className="text-white font-semibold mb-4">{t('common.products')}</h4>
                        <ul className="space-y-2 text-sm">
                            {categories.length > 0 ? (
                                categories.map((cat) => (
                                    <li key={cat.id}>
                                        <Link to={`/category/${cat.slug}`} className="hover:text-primary transition-colors">
                                            {isRTL ? cat.name_ar : cat.name_fr}
                                        </Link>
                                    </li>
                                ))
                            ) : (
                                <>
                                    <li><Link to="/category/bio" className="hover:text-primary transition-colors">Bio</Link></li>
                                    <li><Link to="/category/cosmetique" className="hover:text-primary transition-colors">Cosmétique</Link></li>
                                    <li><Link to="/category/herbes" className="hover:text-primary transition-colors">Herbes</Link></li>
                                </>
                            )}
                        </ul>
                    </div>

                    {/* Contact */}
                    <div>
                        <h4 className="text-white font-semibold mb-4">{t('common.contact')}</h4>
                        <ul className="space-y-2 text-sm">
                            <li>{getSetting('contact_address', 'Marrakech, Maroc')}</li>
                            <li>06.60.84.07.32</li>
                            <li>{getSetting('contact_email', 'contact@farhan.ma')}</li>
                        </ul>
                    </div>

                    {/* Social */}
                    <div>
                        <h4 className="text-white font-semibold mb-4">Suivez-nous</h4>
                        <div className="flex space-x-4 rtl:space-x-reverse">
                            {/* Facebook */}
                            <a href="https://web.facebook.com/farhanebio/" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-neutral-700 rounded-full flex items-center justify-center hover:bg-[#1877F2] transition-colors cursor-pointer group">
                                <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                                    <path d="M18 2h-3a5 5 0 00-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 011-1h3z" />
                                </svg>
                            </a>
                            {/* Instagram */}
                            <a href="https://www.instagram.com/farhancentre" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-neutral-700 rounded-full flex items-center justify-center hover:bg-[#E4405F] transition-colors cursor-pointer group">
                                <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M16 11.37A4 4 0 1112.63 8 4 4 0 0116 11.37zm1.5-4.87h.01M7.5 3h9a4.5 4.5 0 014.5 4.5v9a4.5 4.5 0 01-4.5 4.5h-9A4.5 4.5 0 013 19.5v-9A4.5 4.5 0 017.5 3z" />
                                </svg>
                            </a>
                            {/* TikTok */}
                            <a href="#" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-neutral-700 rounded-full flex items-center justify-center hover:bg-[#000000] transition-colors cursor-pointer group">
                                <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                                    <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1-.1z" />
                                </svg>
                            </a>
                        </div>
                    </div>
                </div>
                <div className="border-t border-neutral-800 mt-12 pt-8 text-center text-sm text-neutral-500">
                    &copy; {new Date().getFullYear()} {getSetting('site_name', 'Farhan')}. All rights reserved.
                </div>
            </div>
        </footer>
    );
};

export default Footer;
